import React, { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/categories";
import api from "@/lib/api";
import { toast } from "sonner";

export function PostComposer({ onPosted, defaultCategory = "general", compact = false }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [submitting, setSubmitting] = useState(false);

  const availableCategories = CATEGORIES.filter(
    (c) => c.key !== "announcements" || user?.role === "admin"
  );

  const submit = async () => {
    if (!content.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/posts", { content: content.trim(), category });
      setContent("");
      toast.success("Posted to " + category);
      onPosted && onPosted(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not post";
      toast.error(typeof msg === "string" ? msg : "Could not post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-border cornell-shadow p-4" data-testid="post-composer">
      <div className="flex gap-3">
        <UserAvatar user={user || {}} size={40} />
        <div className="flex-1">
          <Textarea
            placeholder={`What's on your mind, ${user?.first_name || "Cornellian"}?`}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={compact ? 2 : 3}
            className="resize-none border-0 shadow-none focus-visible:ring-0 px-0 text-[15px]"
            maxLength={2000}
            data-testid="composer-content-input"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] h-9" data-testid="composer-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`composer-category-option-${c.key}`}>
                    <span className="mr-2">{c.icon}</span>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={submit}
              disabled={!content.trim() || submitting}
              className="bg-[#B31B1B] hover:bg-[#8f1616] rounded-full px-5"
              data-testid="composer-submit-btn"
            >
              {submitting ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
